/**
 * CardActionButtons – four action buttons under the HealthPass card
 *
 * 1. CHECK-IN    – QR for a travel provider's check-in (valid 1h)
 * 2. EMERGENCY   – instant access for first responders (offline + online)
 * 3. MED ACCESS  – full access for registered doctors (48h by default)
 * 4. WALLET      – Opens the user wallet or card overview
 */

import {
  CheckCircle,
  Loader2,
  QrCode,
  Shield,
  Stethoscope,
  Wallet,
  X,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import apiClient from "../../services/api/apiClient";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";

// ─── Shared helpers ───────────────────────────────────────────────────────────

function QRModal({
  title,
  subtitle,
  qrDataUrl,
  url,
  onClose,
  children,
}: {
  title: string;
  subtitle: string;
  qrDataUrl: string;
  url: string;
  onClose: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useI18nStore();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={t.close}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <img
            src={qrDataUrl}
            alt={t.cardxQrAlt}
            className="w-52 h-52 rounded-xl border border-gray-200"
          />
        </div>

        {/* URL copy */}
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-600 flex-1 truncate">{url}</span>
          <button
            onClick={copy}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
          >
            {copied ? `✓ ${t.copied}` : t.copy}
          </button>
        </div>

        {children}

        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
        >
          {t.close}
        </button>
      </div>
    </div>
  );
}

// ─── 1. CHECK-IN ─────────────────────────────────────────────────────────────

function CheckInModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [bookingCode, setBookingCode] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    qrDataUrl: string;
    accessUrl: string;
    expiresAt: string;
  } | null>(null);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!bookingCode.trim()) {
      setError(t.cardxBookingCodeRequired);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await apiClient.post("/checkin/generate", {
        operatorType: "hotel",
        operatorName: operatorName.trim() || undefined,
        bookingRef: bookingCode.trim(),
        dataScope: ["base", "emergency", "vaccinations"],
        expiryHours: 1,
      });
      setResult(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? t.cardxQrGenerateFailed);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const expiryLabel = fmt.time(result.expiresAt);
    return (
      <QRModal
        title={t.cardxCheckinQrTitle}
        subtitle={t.cardxValidOneHourUntil.replace("{time}", expiryLabel)}
        qrDataUrl={result.qrDataUrl}
        url={result.accessUrl}
        onClose={onClose}
      >
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-center">
          {t.cardxCheckinScanHint}
        </p>
      </QRModal>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {t.cardxCheckinTitle}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {t.cardxCheckinSubtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={t.close}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label
              htmlFor="booking-code"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t.cardxBookingCode} <span className="text-red-500">*</span>
            </label>
            <input
              id="booking-code"
              type="text"
              value={bookingCode}
              onChange={(e) => setBookingCode(e.target.value.toUpperCase())}
              placeholder={t.cardxBookingCodePlaceholder}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              // The dialog exists solely to collect this code, so focusing it
              // on open is the expected behaviour rather than a focus trap.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </div>
          <div>
            <label
              htmlFor="provider-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t.cardxProviderName}{" "}
              <span className="text-gray-400 font-normal">
                {t.cardxOptionalParen}
              </span>
            </label>
            <input
              id="provider-name"
              type="text"
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              placeholder={t.cardxProviderPlaceholder}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t.cancel}
          </button>
          <button
            onClick={generate}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <QrCode className="w-4 h-4" />
            )}
            {t.cardxGenerateQr}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 2. EMERGENCY ─────────────────────────────────────────────────────────────

function EmergencyModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18nStore();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    qrDataUrl: string;
    onlineUrl: string;
  } | null>(null);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.post("/emergency/qr/generate");
      const data = res.data.data;
      setResult({
        qrDataUrl: data.qrCodeDataUrl ?? data.qrDataUrl,
        onlineUrl: data.accessUrl ?? data.onlineUrl,
      });
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? t.cardxEmergencyQrFailed);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    generate();
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          <p className="text-sm text-gray-600">
            {t.cardxEmergencyQrGenerating}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
          <p className="text-red-600 text-sm">{error}</p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm"
            >
              {t.cancel}
            </button>
            <button
              onClick={generate}
              className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm"
            >
              {t.cardxRetry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <QRModal
      title={t.cardxEmergencyQrTitle}
      subtitle={t.cardxEmergencyQrSubtitle}
      qrDataUrl={result.qrDataUrl}
      url={result.onlineUrl}
      onClose={onClose}
    >
      <div className="bg-red-50 rounded-lg px-3 py-2 space-y-1">
        <p className="text-xs font-medium text-red-700">
          ⚡ {t.cardxEmergencyNoLogin}
        </p>
        <p className="text-xs text-red-600">{t.cardxEmergencyAuditNote}</p>
      </div>
    </QRModal>
  );
}

// ─── 3. MED ACCESS ───────────────────────────────────────────────────────────

function MedAccessModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [expiryHours, setExpiryHours] = useState(48);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    qrDataUrl: string;
    accessUrl: string;
    expiresAt: string;
    expiryHours: number;
  } | null>(null);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.post("/med-access/generate", { expiryHours });
      setResult(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? t.cardxMedAccessFailed);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const expiry = fmt.dateTime(result.expiresAt, "medium");
    return (
      <QRModal
        title={t.cardxMedAccessQrTitle}
        subtitle={t.cardxValidHoursUntil
          .replace("{hours}", String(result.expiryHours))
          .replace("{time}", expiry)}
        qrDataUrl={result.qrDataUrl}
        url={result.accessUrl}
        onClose={onClose}
      >
        <div className="bg-blue-50 rounded-lg px-3 py-2 space-y-1">
          <p className="text-xs font-medium text-blue-700">
            <CheckCircle className="w-3 h-3 inline mr-1" />
            {t.cardxDoctorMustBeRegistered}
          </p>
          <p className="text-xs text-blue-600">
            {t.cardxConsentNotificationNote}
          </p>
        </div>
      </QRModal>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {t.cardxMedAccessTitle}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {t.cardxMedAccessSubtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={t.close}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Expiry selector */}
        <div>
          {/* Not a <label>: this names a group of buttons, not a form control,
              so a labelled group is the correct markup. */}
          <span
            id="access-duration-label"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t.cardxAccessDuration}
          </span>
          <div
            role="group"
            aria-labelledby="access-duration-label"
            className="grid grid-cols-4 gap-2"
          >
            {[
              { label: t.cardxDurationHours.replace("{n}", "6"), value: 6 },
              { label: t.cardxDurationHours.replace("{n}", "24"), value: 24 },
              { label: t.cardxDurationHours.replace("{n}", "48"), value: 48 },
              { label: t.cardxDurationDays.replace("{n}", "7"), value: 168 },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setExpiryHours(opt.value)}
                className={`py-2 rounded-lg text-sm font-medium transition-colors border ${
                  expiryHours === opt.value
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            {t.cardxAccessExpiresNote}
          </p>
        </div>

        <div className="bg-amber-50 rounded-lg px-3 py-2">
          <p className="text-xs text-amber-700">{t.cardxDoctorAccountNote}</p>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t.cancel}
          </button>
          <button
            onClick={generate}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <QrCode className="w-4 h-4" />
            )}
            {t.cardxGenerateQr}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

type ActiveModal = "checkin" | "emergency" | "medaccess" | null;

interface CardActionButtonsProps {
  /** "grid" = 2×2 grid (CardPage default), "horizontal" = single row (Dashboard) */
  layout?: "grid" | "horizontal";
  /** Override for the WALLET button – navigates instead of "coming soon" */
  onWalletClick?: () => void;
}

export function CardActionButtons({
  layout = "grid",
  onWalletClick,
}: CardActionButtonsProps) {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t } = useI18nStore();

  if (!user) return null;

  const isHorizontal = layout === "horizontal";
  const handleWalletNavigation =
    onWalletClick ?? (() => navigate("/traveler/card/wallet"));

  // One round tile per action, label underneath (UX_CONCEPT.md §5.1). Red is
  // the emergency colour and nothing else, so only Emergency is filled.
  const actions = [
    {
      key: "checkin",
      label: t.cardxActionCheckin,
      hint: t.cardxActionCheckinHint,
      Icon: QrCode,
      tile: "bg-primary-100 text-primary-600",
      onClick: () => setActiveModal("checkin"),
    },
    {
      key: "emergency",
      label: t.cardxActionEmergency,
      hint: t.cardxActionEmergencyHint,
      Icon: Shield,
      tile: "bg-emergency text-white shadow-[0_8px_18px_-8px_rgba(212,42,30,0.7)]",
      onClick: () => setActiveModal("emergency"),
    },
    {
      key: "medaccess",
      label: t.cardxActionMedAccess,
      hint: t.cardxActionMedAccessHint,
      Icon: Stethoscope,
      tile: "bg-health-100 text-health-600",
      onClick: () => setActiveModal("medaccess"),
    },
    {
      key: "wallet",
      label: t.cardxActionWallet,
      hint: t.cardxActionWalletHint,
      Icon: Wallet,
      tile: "bg-muted text-ink",
      onClick: handleWalletNavigation,
    },
  ] as const;

  return (
    <>
      <div className="grid w-full grid-cols-4 gap-2">
        {actions.map(({ key, label, hint, Icon, tile, onClick }) => (
          <button
            key={key}
            type="button"
            onClick={onClick}
            className="group flex flex-col items-center gap-2 rounded-2xl px-1 py-1 text-ink"
          >
            <span
              className={`flex h-14 w-14 items-center justify-center rounded-[20px] transition-transform group-hover:-translate-y-0.5 group-active:scale-95 motion-reduce:transition-none ${tile}`}
            >
              <Icon className="h-6 w-6" aria-hidden="true" />
            </span>
            <span className="text-xs font-semibold">{label}</span>
            {!isHorizontal && (
              <span className="text-center text-[11px] leading-tight text-muted-foreground">
                {hint}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Modals */}
      {activeModal === "checkin" && (
        <CheckInModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "emergency" && (
        <EmergencyModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal === "medaccess" && (
        <MedAccessModal onClose={() => setActiveModal(null)} />
      )}
    </>
  );
}
